<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { uniqueId } from 'lodash-es';
import CircleCloseIcon from '@/assets/icons/shared/circle/close.svg';
import {
  BANK_BIK_MAX_LENGTH,
  BANK_CORRESPONDENT_ACCOUNT_MAX_LENGTH,
  BANK_SETTLEMENT_ACCOUNT_MAX_LENGTH,
  COMMENT_MAX_LENGTH,
} from '@/modules/common/consts/max-length.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import {
  BANK_DETAILS_DIGITS_MASK,
  useMasterBankDetailsForm,
} from '@/modules/counterparty/counterparty-master/composables/use-master-bank-details-form.ts';
import type {
  CounterpartyBankDetailsFormData,
  CounterpartyBankDetailsItem,
} from '@/modules/counterparty/counterparty-master/types/bank-details-form.ts';
import { OptionsHelper } from '@/modules/counterparty/utils/options-helper.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import Validator from '@/modules/common/utils/validator.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import AddButton from '@/modules/core/components/buttons/AddButton.vue';
import CloseButton from '@/modules/ui/components/buttons/CloseButton.vue';
import SaveButton from '@/modules/ui/components/buttons/SaveButton.vue';
import ModalForm from '@/modules/ui/components/common/ModalForm.vue';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';

const PLACEHOLDERS = {
  bank: 'counterparty.master.steps.bankDetails.field.bank',
  bik: 'counterparty.master.steps.bankDetails.field.bik',
  correspondentAccount: 'counterparty.master.steps.bankDetails.field.correspondentAccount',
  settlementAccount: 'counterparty.master.steps.bankDetails.field.settlementAccount',
  comment: 'ui.common.placeholder.enterNote',
} as const;

const isOpen = defineModel<boolean>({ required: true });

const props = defineProps<{
  editItem?: CounterpartyBankDetailsItem | null;
  loading?: boolean;
}>();

const emit = defineEmits<{
  submit: [value: CounterpartyBankDetailsFormData];
}>();

const { INITIAL_FORM_DATA, formData } = useMasterBankDetailsForm();
const { tooltipsDisabled, onInput } = useFormTooltips();

const showErrors = ref(false);

const isEdit = computed(() => !!props.editItem);

const title = computed(() =>
  isEdit.value
    ? 'counterparty.master.steps.bankDetails.form.editTitle'
    : 'counterparty.master.steps.bankDetails.form.title'
);

const hasBank = computed(() => !!formData.value.bank);

const errors = computed(() => ({
  bank: Validator.required(formData.value.bank),
  bankIdentificationCode: Validator.required(formData.value.bankIdentificationCode),
  correspondentAccount: Validator.required(formData.value.correspondentAccount),
  currentAccount: Validator.required(formData.value.currentAccount.trim()),
}));

const isValid = computed(() => !Object.values(errors.value).some(Boolean));

const fieldIds = {
  bank: uniqueId(IdPrefix.Control),
  bik: uniqueId(IdPrefix.Control),
  correspondentAccount: uniqueId(IdPrefix.Control),
  settlementAccount: uniqueId(IdPrefix.Control),
  comment: uniqueId(IdPrefix.Control),
};

function resetForm() {
  formData.value = { ...INITIAL_FORM_DATA };
  showErrors.value = false;
}

function clearBankDetails() {
  formData.value = {
    ...formData.value,
    bank: null,
    bankIdentificationCode: '',
    correspondentAccount: '',
    currentAccount: '',
  };
  showErrors.value = false;
}

function applyFormFromEditItem() {
  if (!props.editItem) {
    resetForm();
    return;
  }

  formData.value = {
    bank: {
      ...props.editItem.bank,
      source: { ...props.editItem.bank.source },
    },
    bankIdentificationCode: props.editItem.bankIdentificationCode,
    correspondentAccount: props.editItem.correspondentAccount,
    currentAccount: props.editItem.currentAccount,
    comment: props.editItem.comment,
  };
  showErrors.value = false;
}

watch(isOpen, (open) => {
  if (!open) {
    return;
  }

  applyFormFromEditItem();
});

function submit() {
  if (props.loading) {
    return;
  }

  showErrors.value = !isValid.value;

  if (!isValid.value || !formData.value.bank) {
    return;
  }

  emit('submit', {
    bank: formData.value.bank,
    bankIdentificationCode: formData.value.bankIdentificationCode,
    correspondentAccount: formData.value.correspondentAccount,
    currentAccount: formData.value.currentAccount.trim(),
    comment: formData.value.comment.trim(),
  });
}
</script>

<template>
  <ModalForm
    v-model:open="isOpen"
    width="s"
    :confirm-on-dirty="false"
    :title
  >
    <template #body>
      <div class="bank-details-form">
        <UiField
          :id="fieldIds.bank"
          stretch
          class="bank-field"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.bank, formData.bank)"
        >
          <UiCombobox
            v-model="formData.bank"
            :placeholder="PLACEHOLDERS.bank"
            :options="OptionsHelper.banksCBRF"
            :error="showErrors && !!errors.bank"
            :disabled="loading"
            no-clear
            with-search
            search-by-code
            inline-search
            expanded
            :search-max-length="COMMENT_MAX_LENGTH"
          />
        </UiField>

        <section
          v-if="hasBank"
          class="bank-requisites-block"
        >
          <div class="header">
            <h3 class="title">{{ $t('counterparty.master.steps.bankDetails.form.bankDetailsTitle') }}</h3>

            <UiButton
              class="clear"
              color="black"
              mode="plain"
              size="s"
              :disabled="loading"
              @click="clearBankDetails"
            >
              <CircleCloseIcon v-prefix />
              <span>{{ $t('counterparty.master.steps.bankDetails.form.clear') }}</span>
            </UiButton>
          </div>

          <div class="fields">
            <UiField
              :id="fieldIds.bik"
              stretch
              with-tooltip
              :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.bik, formData.bankIdentificationCode)"
            >
              <UiInput
                :id="fieldIds.bik"
                v-model="formData.bankIdentificationCode"
                :placeholder="PLACEHOLDERS.bik"
                :max-length="BANK_BIK_MAX_LENGTH"
                :mask="BANK_DETAILS_DIGITS_MASK.bik"
                :error="showErrors && !!errors.bankIdentificationCode"
                readonly
              />
            </UiField>

            <UiField
              :id="fieldIds.correspondentAccount"
              stretch
              with-tooltip
              :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.correspondentAccount, formData.correspondentAccount)"
            >
              <UiInput
                :id="fieldIds.correspondentAccount"
                v-model="formData.correspondentAccount"
                :placeholder="PLACEHOLDERS.correspondentAccount"
                :max-length="BANK_CORRESPONDENT_ACCOUNT_MAX_LENGTH"
                :mask="BANK_DETAILS_DIGITS_MASK.correspondentAccount"
                :error="showErrors && !!errors.correspondentAccount"
                readonly
              />
            </UiField>

            <UiField
              :id="fieldIds.settlementAccount"
              stretch
              with-tooltip
              :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.settlementAccount, formData.currentAccount)"
              :tooltip-disabled="tooltipsDisabled"
              :error="showErrors && errors.currentAccount ? errors.currentAccount : undefined"
            >
              <UiInput
                :id="fieldIds.settlementAccount"
                v-model="formData.currentAccount"
                :placeholder="PLACEHOLDERS.settlementAccount"
                :max-length="BANK_SETTLEMENT_ACCOUNT_MAX_LENGTH"
                :mask="BANK_DETAILS_DIGITS_MASK.settlementAccount"
                :error="showErrors && !!errors.currentAccount"
                :disabled="loading"
                @update:model-value="onInput"
              />
            </UiField>
          </div>
        </section>

        <UiField
          :id="fieldIds.comment"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.comment, formData.comment)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiTextarea
            :id="fieldIds.comment"
            v-model="formData.comment"
            :placeholder="PLACEHOLDERS.comment"
            :max-length="COMMENT_MAX_LENGTH"
            :rows="3"
            no-line-breaks
            :disabled="loading"
            @update:model-value="onInput"
          />
        </UiField>
      </div>
    </template>

    <template #footer>
      <CloseButton
        :disabled="loading"
        @click="isOpen = false"
      />

      <SaveButton
        v-if="isEdit"
        :loading="loading"
        @click="submit"
      />
      <AddButton
        v-else
        no-icon
        :loading="loading"
        @click="submit"
      />
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.bank-details-form {
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  gap: 12px;
}

.bank-requisites-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 12px;
  border: var(--border-gray-200);
  border-radius: 16px;
  background-color: var(--color-white);
}

.header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.title {
  margin: 0;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  font-weight: var(--font-weight-bold);
}

.clear {
  flex-shrink: 0;
  margin: -4px -8px -4px 0;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
</style>
